import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useMemo } from 'react';
import { parseISO, startOfWeek, endOfWeek, format, getYear, getISOWeek } from 'date-fns';

const formatSignedDollars = (value) => {
	const formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(value || 0));
	return (value || 0) >= 0 ? `+${formatted}` : `-${formatted}`;
};

export default function WeeklyPnlTiles({ data }) {
	const [currentYear, setCurrentYear] = useState(() => {
		if (!data || data.length === 0) return new Date().getFullYear();
		const lastDate = parseISO(data[data.length - 1].date);
		return getYear(lastDate);
	});

	const weeklyData = useMemo(() => {
		if (!data || data.length === 0) return [];

		const weekMap = {};
		data.forEach((d) => {
			const date = parseISO(d.date);
			const ws = startOfWeek(date, { weekStartsOn: 1 });
			const key = format(ws, 'yyyy-MM-dd');
			if (!weekMap[key]) {
				weekMap[key] = { weekStart: ws, pnl: 0, trades: 0 };
			}
			weekMap[key].pnl += d.profit || 0;
			weekMap[key].trades += d.trades || 0;
		});

		return Object.values(weekMap)
			.filter((w) => getYear(w.weekStart) === currentYear)
			.sort((a, b) => a.weekStart - b.weekStart);
	}, [data, currentYear]);

	const maxPnl = useMemo(() => {
		if (weeklyData.length === 0) return 1;
		return Math.max(...weeklyData.map((w) => Math.abs(w.pnl)), 1);
	}, [weeklyData]);

	const yearStats = useMemo(() => {
		let totalPnl = 0;
		let totalTrades = 0;
		for (const w of weeklyData) {
			totalPnl += w.pnl;
			totalTrades += w.trades;
		}
		return { totalPnl, totalTrades, weeks: weeklyData.length };
	}, [weeklyData]);

	if (!data || data.length === 0) {
		return (
			<Card>
				<CardHeader><CardTitle className="text-sm">Weekly P&L</CardTitle></CardHeader>
				<CardContent>
					<div className="text-muted-foreground flex min-h-[200px] items-center justify-center text-sm">No data available</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader className="pb-3">
				<div className="flex items-center justify-between">
					<CardTitle className="text-sm">Weekly P&L</CardTitle>
					<div className="flex items-center gap-3">
						<span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${yearStats.totalPnl >= 0 ? 'bg-emerald-500/20 text-emerald-500' : 'bg-red-500/20 text-red-500'}`}>
							{formatSignedDollars(yearStats.totalPnl)}
						</span>
						<span className="text-muted-foreground text-xs">{yearStats.totalTrades}T</span>
						<span className="text-muted-foreground text-xs">{yearStats.weeks} weeks</span>
					</div>
				</div>
				<div className="mt-2 flex items-center gap-2">
					<Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCurrentYear((y) => y - 1)}>
						<ChevronLeft className="h-4 w-4" />
					</Button>
					<span className="min-w-[60px] text-center text-sm font-medium">{currentYear}</span>
					<Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCurrentYear((y) => y + 1)}>
						<ChevronRight className="h-4 w-4" />
					</Button>
					<Button variant="outline" size="sm" className="ml-1 h-7 text-xs" onClick={() => setCurrentYear(new Date().getFullYear())}>
						This Year
					</Button>
				</div>
			</CardHeader>
			<CardContent>
				<div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6 lg:grid-cols-8">
					{weeklyData.map((w) => {
						const intensity = Math.min(1, Math.abs(w.pnl) / maxPnl);
						const isPositive = w.pnl >= 0;
						const bgColor = isPositive
							? `rgba(34, 197, 94, ${0.12 + intensity * 0.5})`
							: `rgba(239, 68, 68, ${0.12 + intensity * 0.5})`;
						const we = endOfWeek(w.weekStart, { weekStartsOn: 1 });
						const weekNum = getISOWeek(w.weekStart);

						return (
							<div
								key={format(w.weekStart, 'yyyy-MM-dd')}
								className="flex flex-col items-center justify-center rounded p-1.5"
								style={{ background: bgColor }}
								title={`W${weekNum}: ${format(w.weekStart, 'MMM d')} – ${format(we, 'MMM d')}: ${formatSignedDollars(w.pnl)} (${w.trades} trades)`}
							>
								<span className="text-foreground/50 text-[9px]">W{weekNum}</span>
								<span className="text-[10px] font-bold text-foreground/90">{formatSignedDollars(w.pnl)}</span>
								<span className="text-foreground/50 text-[9px]">{w.trades}T</span>
							</div>
						);
					})}
				</div>
			</CardContent>
		</Card>
	);
}
